import { CheckIcon } from "@heroicons/react/20/solid";
import type { ChangeEvent, ReactNode } from "react";
import { useId } from "react";

type CheckboxProps = {
  id?: string;
  status?: boolean;
  title?: string;
  onChange: (event: boolean) => void;
  disabled?: boolean;
  required?: boolean;
};

export const Checkbox = ({
  id,
  status = false,
  disabled = false,
  required = false,
  title,
  onChange,
}: CheckboxProps): ReactNode => {
  // Callers may render their own <label htmlFor={id}> next to the checkbox, so a caller-provided
  // id always wins; the generated fallback only keeps `title` associated when none is given.
  const generatedId = useId();
  const checkboxId = id ?? generatedId;

  return (
    <div className="flex items-center text-neutral-700 mb-2">
      <div className="relative flex flex-shrink-0 mr-2">
        <input
          type="checkbox"
          id={checkboxId}
          checked={status}
          disabled={disabled}
          required={required}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
          className={`
                        appearance-none bg-white border w-8 h-8 rounded shadow m-0
                        ${disabled ? "cursor-default border-neutral-400" : "cursor-pointer border-primary"}
                    `}
        />
        {/* Purely decorative: the native input underneath owns focus, keyboard and validation */}
        <CheckIcon
          aria-hidden={true}
          data-testid="checkbox-icon"
          className={`
                        absolute inset-0 m-auto h-5 w-5 pointer-events-none
                        ${status ? "block" : "hidden"}
                        ${disabled ? "text-neutral-400" : "text-primary"}
                    `}
        />
      </div>
      {title && (
        <label
          htmlFor={checkboxId}
          className={`text-sm ml-2 text-left ${disabled ? "text-neutral-500 cursor-default" : "cursor-pointer"}`}
        >
          {title}
        </label>
      )}
    </div>
  );
};
